/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { Portal } from '@gorhom/portal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import * as Layout from '@components/screen/Layout';
import SelectBox from '@components/form/SelectBox';
import SociollaText from '@components/sociolla/SociollaText';
import SociollaCard from '@components/sociolla/SociollaCard';
import ProgressChart from '@components/chart/ProgressChart';
import ProductDashboard from '@/screens/home/components/snippets/ProductDashboard';

import { isWeb, fullWidth } from '@global/screens';
import Colors from '@global/colors';
import {
  getTodayMonth,
  changeTimeZone,
  convertTargetSettingValue,
  targetCalculation,
  currency,
} from '@global/helpers';
import { FILTER_PERIOD, SALESMAN_TYPE, MONTH_NAME_SIMPLE } from '@/constants';

import { Dashboard } from '@api/dashboard';

const propTypes = {
  data: PropTypes.array,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onBack: PropTypes.func,
};

const defaultProps = {
  data: [],
  style: {},
  onBack: () => {},
};

const ProductFocusFilter = React.memo(
  ({
    topSpace = 0,
    filterPeriod = '',
    filterSalesmanType = '',
    onChange = () => {},
  }) => {
    return (
      <View
        style={[
          styles.fullWidth,
          styles.filterRow,
          !isWeb ? { top: topSpace } : {},
        ]}>
        {isWeb ? (
          <SociollaText type="medium" style={styles.titlePage}>
            Product Focus by Brand
          </SociollaText>
        ) : (
          false
        )}
        <View style={[styles.row, styles.filterWrapper]}>
          <View
            style={[styles.row, !isWeb ? styles.filterSalesmanTypeMobile : {}]}>
            <SociollaText style={!isWeb ? styles.filterText : {}}>
              Salesman Type
            </SociollaText>
            <View style={styles.filterPeriod}>
              <SelectBox
                label="Salesman Type"
                hideLabel
                options={SALESMAN_TYPE}
                value={filterSalesmanType}
                placeholder="Select Salesman Type"
                trackBy="value"
                labelKey="label"
                isDropdown={isWeb}
                onChange={(value) => {
                  onChange({ type: 'salesman_type', value });
                }}
              />
            </View>
          </View>
          <View style={[styles.row, styles.filterSpace]}>
            <SociollaText style={!isWeb ? styles.filterText : {}}>
              Month
            </SociollaText>
            <View style={styles.filterPeriod}>
              <SelectBox
                label="Month"
                hideLabel
                options={FILTER_PERIOD}
                value={filterPeriod}
                placeholder="Select Month"
                trackBy="value"
                labelKey="label"
                isDropdown={isWeb}
                onChange={(value) => {
                  onChange({ type: 'month', value });
                }}
              />
            </View>
          </View>
        </View>
      </View>
    );
  },
  (prev, next) => prev == next,
);

const ProductFocus = (props) => {
  const today = changeTimeZone(new Date());
  const today_month_name = MONTH_NAME_SIMPLE[today.getMonth()];
  const { onBack } = props;
  const insets = useSafeAreaInsets();

  const todayMonth = getTodayMonth();
  const defaultFilter = todayMonth ? todayMonth : FILTER_PERIOD[0];

  const [filterPeriod, setFilterPeriod] = useState(defaultFilter);
  const [filterSalesmanType, setFilterSalefilterSalesmanType] = useState(
    SALESMAN_TYPE[0],
  );
  const [loading, setLoading] = useState(false);
  const [dataProductFocus, setDataProductFocus] = useState([]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      getProductFocusDashboard();
    }
    return () => {
      mounted = false;
    };
  }, [filterPeriod, filterSalesmanType]);

  const getProductFocusDashboard = async () => {
    setLoading(true);
    try {
      const payload = {
        params: {
          filter: {
            year: today.getFullYear(),
            month: Number(filterPeriod?.month),
            salesman_type: filterSalesmanType?.value,
          },
        },
      };
      const response = await Dashboard.getDashboardData(
        'product-focus',
        'salesman',
        payload,
      );
      if (response?.data?.data) {
        const res = response.data.data;
        const arr = Object.keys(res);
        const result = [];
        for (let i = 0; i < arr.length; i++) {
          const key = arr[i];
          const item = res[key];
          if (item && item?.brand?.name && item?.product) {
            result.push({
              ...item,
              ...(item?.product || {}),
            });
          }
        }
        setDataProductFocus(result);
      } else {
        setDataProductFocus([]);
      }
      setLoading(false);
    } catch (err) {
      console.log('err', err);
      setDataProductFocus([]);
      setLoading(false);
    }
  };

  return (
    <>
      {isWeb && <Layout.Back title="Dashboard" onCallback={() => onBack()} />}
      {/* <View style={[styles.fullWidth, styles.rowBetween, styles.filterRow]}> */}
      {isWeb ? (
        <ProductFocusFilter
          filterPeriod={filterPeriod}
          filterSalesmanType={filterSalesmanType}
          onChange={({ type, value }) => {
            if (type == 'month') {
              setFilterPeriod(value);
            } else {
              setFilterSalefilterSalesmanType(value);
            }
          }}
        />
      ) : (
        <Portal name="filter-product-focus">
          <ProductFocusFilter
            topSpace={insets.top + 55}
            filterPeriod={filterPeriod}
            filterSalesmanType={filterSalesmanType}
            onChange={({ type, value }) => {
              if (type == 'month') {
                setFilterPeriod(value);
              } else {
                setFilterSalefilterSalesmanType(value);
              }
            }}
          />
        </Portal>
      )}
      {/* <View style={[styles.fullWidth, styles.filterRow]}>
        {isWeb ? (
          <SociollaText type="medium" style={styles.titlePage}>
            Product Focus by Brand
          </SociollaText>
        ) : (
          false
        )}
        <View style={[styles.row, styles.filterWrapper]}>
          {
            isWeb ? (
              <View style={styles.row}>
                <SociollaText style={!isWeb ? styles.filterText : {}}>
                  Salesman Type
                </SociollaText>
                <View style={styles.filterPeriod}>
                  <SelectBox
                    label="Salesman Type"
                    hideLabel
                    options={SALESMAN_TYPE}
                    value={SALESMAN_TYPE[0]}
                    placeholder="Select Salesman Type"
                    trackBy="value"
                    labelKey="label"
                    isDropdown={isWeb}
                    onChange={(value) => {
                      setFilterPeriod(value);
                    }}
                  />
                </View>
              </View>
            ) : false
          }
          <View style={[styles.row, styles.filterSpace]}>
            <SociollaText style={!isWeb ? styles.filterText : {}}>
              Month
            </SociollaText>
            <View style={styles.filterPeriod}>
              <SelectBox
                label="Month"
                hideLabel
                options={FILTER_PERIOD}
                value={filterPeriod}
                placeholder="Select Month"
                trackBy="value"
                labelKey="label"
                isDropdown={isWeb}
                onChange={(value) => {
                  setFilterPeriod(value);
                }}
              />
            </View>
          </View>
        </View>
      </View> */}
      {/* Product Focus Brand Start */}
      {loading ? (
        <Layout.Loader />
      ) : dataProductFocus?.length ? (
        dataProductFocus.map((item, idx) => (
          <View
            style={[
              styles.defaultCardWidth,
              !isWeb && idx == 0 ? styles.mTopCard : {},
            ]}
            key={idx}>
            <SociollaCard
              title={item?.brand?.name || '-'}
              titleStyle={styles.titleStyle}
              onPress={() => null}>
              <ProgressChart
                titleComponent={
                  <ProductDashboard
                    product={item?.product || {}}
                    style={styles.productItem}
                  />
                }
                target={convertTargetSettingValue(
                  item?.target,
                  item?.target_value_type,
                )}
                targetStyle={styles.titleProgressBar}
                barHeight={15}
                containerStyle={styles.progressProduct}
                count={targetCalculation(item?.actual, item?.target)}
                popupText={`${today_month_name}: ${currency(item.actual || 0)}`}
                showPopup
                leftSpace
              />
            </SociollaCard>
          </View>
          // <>
          //   <View style={[styles.defaultCardWidth, !isWeb ? styles.mTopCard : {}]}>
          //     <SociollaCard
          //       title="Ariul"
          //       titleStyle={styles.titleStyle}
          //       onPress={() => null}>
          //       <ProgressChart
          //         title="All Product"
          //         target="Rp 12M"
          //         targetStyle={styles.titleProgressBar}
          //         barHeight={15}
          //         containerStyle={styles.progressProduct}
          //         count={75}
          //       />
          //     </SociollaCard>
          //   </View>

          //   <View style={styles.defaultCardWidth}>
          //     <SociollaCard
          //       title="COSRX"
          //       titleStyle={styles.titleStyle}
          //       onPress={() => null}>
          //       <ProgressChart
          //         titleComponent={<ProductDashboard style={styles.productItem} />}
          //         target="30 DOORS"
          //         targetStyle={styles.titleProgressBar}
          //         barHeight={15}
          //         containerStyle={styles.progressProduct}
          //         count={40}
          //       />
          //       <ProgressChart
          //         titleComponent={<ProductDashboard style={styles.productItem} />}
          //         target="30 DOORS"
          //         targetStyle={styles.titleProgressBar}
          //         barHeight={15}
          //         containerStyle={styles.progressProduct}
          //         activeColor={Colors.yellow0}
          //         count={85}
          //       />
          //       <ProgressChart
          //         titleComponent={<ProductDashboard style={styles.productItem} />}
          //         target="20 DOORS"
          //         targetStyle={styles.titleProgressBar}
          //         barHeight={15}
          //         containerStyle={styles.progressProduct}
          //         activeColor={Colors.blue4}
          //         count={60}
          //       />
          //     </SociollaCard>
          //   </View>
          // </>
        ))
      ) : (
        <View style={[styles.defaultCardWidth, styles.noData]}>
          <SociollaText>No data found. Please use another filter!</SociollaText>
        </View>
      )}
      {/* Product Focus Brand End */}
    </>
  );
};

const styles = StyleSheet.create({
  defaultCardWidth: { width: '100%' },
  mTopCard: { marginTop: 135 },
  fullWidth: isWeb ? { width: '100%' } : { width: fullWidth },
  // rowBetween: {
  //   flexDirection: 'row',
  //   justifyContent: 'space-between',
  //   marginBottom: 20,
  //   marginTop: 10,
  // },
  titleStyle: {
    color: Colors.black0,
    fontSize: 15,
    lineHeight: 22,
  },
  progressProduct: {
    marginTop: 5,
    marginBottom: 15,
    paddingBottom: 15,
  },
  row: { flexDirection: 'row', alignItems: 'center' },
  filterWrapper: {
    marginTop: isWeb ? 15 : 0,
    ...(!isWeb ? { flexDirection: 'column' } : {}),
  },
  filterRow: {
    // alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 5,
    padding: 15,
    marginBottom: 20,
    marginTop: 10,
    ...(!isWeb
      ? {
          width: fullWidth,
          backgroundColor: Colors.white,
          position: 'absolute',
          // top: !isWeb ? 55 : 0,
          left: 0,
          right: 0,
          zIndex: 1,
          elevation: 1,
          marginTop: 0,
          borderBottomColor: Colors.grey0,
          borderBottomWidth: 1,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }
      : { zIndex: 1 }),
  },
  filterSpace: {
    marginLeft: isWeb ? 20 : 0,
  },
  filterText: {
    width: isWeb ? 40 : 100,
  },
  filterPeriod: {
    marginLeft: 15,
    ...(!isWeb
      ? {
          width: fullWidth - 145,
        }
      : {}),
  },
  titlePage: { fontSize: 16 },
  titleProgressBar: isWeb ? { fontSize: 28 } : { fontSize: 25, marginTop: 5 },
  productItem: { paddingHorizontal: 0, paddingTop: 10, paddingBottom: 0 },
  filterSalesmanTypeMobile: { marginBottom: 10 },
  noData: {
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 80,
  },
});

ProductFocus.propTypes = propTypes;
ProductFocus.defaultProps = defaultProps;

export default React.memo(ProductFocus, (prev, next) => prev == next);
